import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type SkillCategory = 'content' | 'data' | 'code' | 'frontend' | 'devops';

interface Skill {
  id: string;
  icon: string;
  name: string;
  command: string;
  category: SkillCategory;
  categoryLabel: string;
  tagline: string;
  description: string;
  usage: string;
  examples: string[];
  steps: string[];
  flags?: { flag: string; desc: string }[];
  notes?: string;
  cost?: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const SKILLS: Skill[] = [
  // ── Content ─────────────────────────────────────────────────────────────────
  {
    id: 'linkedin-post',
    icon: '📢',
    name: 'LinkedIn Post Generator',
    command: '/linkedin-post',
    category: 'content',
    categoryLabel: 'Content',
    tagline: 'Generate AI post + varied image and save to linkedin-posts/',
    cost: '~$0.04/image (gpt-image-1)',
    description:
      'Audits existing posts, picks the next unused rotation angle (or uses your custom topic), generates a 150–200 word post via GPT-4, then picks an image style at random AFTER reading the post text so the image references actual content. Saves .md + .png, commits, and attempts LinkedIn API publish.',
    usage: '/linkedin-post [optional topic]',
    examples: [
      '/linkedin-post',
      '/linkedin-post [Certified Public Accountant]',
      '/linkedin-post [Professional Engineer]',
      '/linkedin-post multi-state licensing complexity',
    ],
    flags: [
      { flag: '--imageStyle photorealistic', desc: 'Force lifestyle photo (default: random)' },
      { flag: '--imageStyle infographic',    desc: 'Force data visualization infographic' },
      { flag: '--imageStyle simple',         desc: 'Force bold minimal typographic graphic' },
      { flag: '--svg',                       desc: 'Use free SVG renderer instead of gpt-image-1' },
      { flag: '--post',                      desc: 'Attempt to publish via LinkedIn API' },
      { flag: '--accentLabel "..."',         desc: 'Override infographic/scene label' },
    ],
    steps: [
      'Audits linkedin-posts/*.md to find used rotation angles',
      'Picks next unused angle or uses your provided topic',
      'Generates 150–200 word post via GPT-4 (pain-point hook, emojis, CTA)',
      'Picks image style randomly: photorealistic | infographic | simple',
      'Generates image via gpt-image-1 — uses post text for data/scene',
      'Saves .md + .png to linkedin-posts/',
      'Shows image for approval, then commits and pushes',
      'Attempts LinkedIn API publish; reminds you to post to company page manually',
    ],
    notes:
      'Company page posting requires manual repost at linkedin.com/company/111897979 (API token has RESTRICTED_MEMBER scope). Use --imageStyle to override the random style selection.',
  },
  {
    id: 'linkedin-delete',
    icon: '🗑️',
    name: 'LinkedIn Post Deleter',
    command: '/linkedin-delete',
    category: 'content',
    categoryLabel: 'Content',
    tagline: 'Delete a published LinkedIn post by URN and clean up local files',
    description:
      'Takes a LinkedIn post URN (printed after /linkedin-post --post), calls the LinkedIn REST API to delete it, removes the local .md and .png files, and commits the cleanup.',
    usage: '/linkedin-delete POST_URN',
    examples: ['/linkedin-delete urn:li:share:7298765432109876543'],
    steps: [
      'Accepts the post URN from /linkedin-post output',
      'Calls DELETE on the LinkedIn REST API',
      'Removes the local .md and .png files',
      'Commits the cleanup and pushes',
    ],
  },

  {
    id: 'marketing-email',
    icon: '📧',
    name: 'Marketing Email Generator',
    command: '/marketing-email',
    category: 'content',
    categoryLabel: 'Content',
    tagline: 'Generate Brevo-ready copy, hero image, and CE infographic for any profession × state(s)',
    cost: '~$0.04/image (gpt-image-1)',
    description:
      'Looks up live CE requirements from the database for each profession×state combination, then generates: ' +
      '(1) Brevo-ready email copy via GPT-4 with exact mandatory topic hours, ' +
      '(2) a photorealistic profession-specific hero image via gpt-image-1, and ' +
      '(3) a CE requirements donut-chart infographic rendered as SVG → PNG via Sharp. ' +
      'Multiple states share one output directory (YYMMDD_[PROF]/) with state-prefixed filenames. ' +
      'Generator scripts are committed to the repo; assets stay local for Brevo import.',
    usage: '/marketing-email PROFESSION STATE [STATE2 STATE3 ...]',
    examples: [
      '/marketing-email RE NY',
      '/marketing-email LAW NY TX CA',
      '/marketing-email ARCH NY CA FL TX',
      '/marketing-email CPA NY NJ',
      '/marketing-email PE NY',
    ],
    steps: [
      'Parses profession + one or more state abbreviations from arguments',
      'Loads live CE requirements (categories, minimums, totals) from DB for each state',
      'Generates Brevo-ready email copy via GPT-4 with exact mandatory topic hours and profession-specific tone',
      'Generates photorealistic hero image via gpt-image-1 (profession + state scene)',
      'Renders CE requirements donut-chart infographic as SVG → PNG via Sharp',
      'Saves [prof]-[state]-copy.md, [prof]-[state]-hero.png, [prof]-[state]-infographic.png to YYMMDD_[PROF]/',
      'Runs sequentially for each state; skips states with no DB requirement data',
      'Commits the TypeScript generator scripts; assets remain local for Brevo import',
    ],
    notes:
      'Output directory: C:\\Users\\jwjang\\Desktop\\25webproj-cec-miscfiles\\emails\\YYMMDD_[PROF]\\. ' +
      'Requires a StateRequirement to exist in the DB — run /research-ce first if needed. ' +
      'Multiple states share one directory with state-prefixed files (e.g. law-ny-copy.md, law-tx-copy.md).',
  },

  // ── Data ─────────────────────────────────────────────────────────────────────
  {
    id: 'research-ce',
    icon: '🔬',
    name: 'Research CE Requirements',
    command: '/research-ce',
    category: 'data',
    categoryLabel: 'Data',
    tagline: 'Deep-research official CE rules from all 50 states and seed them into the database',
    description:
      'Researches continuing education requirements from official state licensing boards for any profession × state combination — single state, a comma-separated list, or all 50 states at once. ' +
      'All-states mode spawns 5 parallel research agents (10 states each) and compiles a single enriched seed script. ' +
      'Matches the depth of the June 2026 DDS enrichment: live minimums (lectureMinCredits), self-study caps (selfDirectedMaxCredits), mandatory topic categories with correct minCredits, carryover rules, and sourced handbook-quality notes. ' +
      'Corrects wrong base data (totals, cycle lengths, deprecated fields) and always fixes legacy records in-place.',
    usage: '/research-ce PROFESSION STATE\n/research-ce PROFESSION STATE1,STATE2,STATE3\n/research-ce PROFESSION all',
    examples: [
      '/research-ce LAW TX',
      '/research-ce ARCH CA,NY,TX',
      '/research-ce PE all',
      '/research-ce CPA FL,GA,NC',
      '/research-ce DDS IL',
    ],
    flags: [
      { flag: 'PROFESSION STATE',          desc: 'Single state — research inline, seed, verify, commit' },
      { flag: 'PROFESSION S1,S2,...',      desc: 'Multi-state — parallel agents for 4+ states, inline for 1–3' },
      { flag: 'PROFESSION all',            desc: 'All 50 states — 5 parallel background agents of 10 states each' },
    ],
    steps: [
      'Parses profession + stateArg; detects single / multi / all mode',
      'All-states: divides 50 states into 5 groups, spawns parallel background agents',
      'Each agent checks existing DB data, then searches official licensing boards (.gov/.state.xx.us)',
      'Extracts: total credits, cycle length, lectureMinCredits (live minimum hrs), selfDirectedMaxCredits, carryover, mandatory topics + minCredits, source URL',
      'Corrects wrong base data found in DB (wrong totals, wrong cycle length, deprecated % fields)',
      'Compiles enriched TypeScript seed script with clean upsert pattern (delete → recreate)',
      'Runs script via ts-node, back-links stateRequirementID on each Category',
      'Verifies inserted data and commits the script',
    ],
    notes:
      'Now covers all 50 states — the full stateID map (AL=1 … WY=50) is embedded in the skill. ' +
      'Deprecated fields (selfStudyMaxPercent, lectureMinPercent) are always set to null in new scripts — use selfDirectedMaxCredits and lectureMinCredits instead. ' +
      'One-time or alternating-cycle requirements get minCredits:0 in the Category with frequency described in the description field.',
  },

  {
    id: 'prospect-list',
    icon: '📋',
    name: 'Prospect List Builder',
    command: '/prospect-list',
    category: 'data',
    categoryLabel: 'Data',
    tagline: 'Build a targeted CSV contact list for a profession across one or more ZIP codes',
    description:
      'Looks up licensed professionals near one or more ZIP codes (attorneys, CPAs, dentists, architects, engineers, etc.) and writes a ready-to-import CSV to ' +
      'C:\\Users\\jwjang\\Desktop\\25webproj-cec-miscfiles\\data\\. ' +
      'When multiple ZIP codes are provided, the pipeline runs for each ZIP in sequence, then deduplicates by name + address and merges everything into one combined CSV. ' +
      'The CSV maps directly to the CEC Tracker ProContact schema — use Import Contacts from CSV in Marketing Email Admin to load the file. ' +
      'The command is stored at the user level (~/.claude/commands/) and is available in every Claude Code session.',
    usage: '/prospect-list [profession] [ZIP] [ZIP2 ZIP3 ...]',
    examples: [
      '/prospect-list lawyer 10001',
      '/prospect-list lawyer 10001 10017 10018',
      '/prospect-list lawyer10001 10017 10018',
      '/prospect-list cpa 77001 77002 77003',
      '/prospect-list dentist 10001',
    ],
    steps: [
      'Parse profession and all 5-digit ZIP codes from the arguments (profession and first ZIP can be joined or separate)',
      'For each ZIP: search Avvo + Justia (lawyers) or NPI registry (healthcare) + web searches for contacts',
      'Collect at least 20 contacts per ZIP; expand to city/county if fewer found',
      'Deduplicate across ZIPs by full_name + address — same person at multiple ZIPs appears once',
      'Write combined CSV to C:\\...\\data\\<profession>_<zip1>[_<zip2>...].csv',
      'Deep email search: fetch each contact\'s website (/contact, /about), then web search if no email found',
      'Report total contacts, emails found, and saved file path',
    ],
    flags: [
      { flag: 'profession',          desc: 'Any profession keyword: lawyer, dentist, architect, cpa, attorney, engineer, nurse, pharmacist, etc.' },
      { flag: 'ZIP [ZIP2 ZIP3 ...]', desc: 'One or more 5-digit US ZIP codes. All contacts are merged and deduplicated into a single CSV.' },
    ],
    notes:
      'Output is always saved to C:\\Users\\jwjang\\Desktop\\25webproj-cec-miscfiles\\data\\. ' +
      'Multi-ZIP filename example: lawyer_10001_10017_10018.csv. ' +
      'Deduplication is by full_name + address — the same attorney at two ZIPs appears only once. ' +
      'No permission prompts — WebSearch, WebFetch, Write, Read, Bash, and PowerShell are all pre-approved for this command.',
  },

  // ── Code Quality ─────────────────────────────────────────────────────────────
  {
    id: 'code-review',
    icon: '🔍',
    name: 'Code Review',
    command: '/code-review',
    category: 'code',
    categoryLabel: 'Code Quality',
    tagline: 'Review the current diff for bugs and cleanup opportunities',
    description:
      'Reviews all staged/unstaged changes on the current branch against main. Reports correctness bugs, dead code, missed reuse opportunities, and inefficiencies at the requested effort level.',
    usage: '/code-review [low|medium|high|ultra]',
    examples: [
      '/code-review',
      '/code-review high',
      '/code-review ultra',
      '/code-review ultra --comment',
      '/code-review --fix',
    ],
    flags: [
      { flag: 'low / medium', desc: 'Fewer, high-confidence findings only' },
      { flag: 'high / ultra', desc: 'Broader coverage; may include uncertain findings' },
      { flag: '--comment',    desc: 'Post findings as inline GitHub PR comments' },
      { flag: '--fix',        desc: 'Auto-apply the findings to the working tree' },
    ],
    steps: [
      'Diffs current branch against main',
      'Scans for correctness bugs, reuse opportunities, efficiency issues',
      'Returns a prioritized findings list',
      'With --fix: applies fixes directly to working tree',
      'With --comment: posts as inline PR review comments',
    ],
  },
  {
    id: 'security-review',
    icon: '🔒',
    name: 'Security Review',
    command: '/security-review',
    category: 'code',
    categoryLabel: 'Code Quality',
    tagline: 'OWASP-focused security audit of pending changes',
    description:
      'Audits all pending branch changes for security vulnerabilities — injection flaws, auth bypasses, insecure data handling, CORS misconfig, and other OWASP Top 10 patterns.',
    usage: '/security-review',
    examples: ['/security-review'],
    steps: [
      'Diffs current branch against main',
      'Checks for OWASP Top 10 vulnerability patterns',
      'Flags auth, input validation, SQL/NoSQL injection, XSS risks',
      'Reports findings with severity levels and fix suggestions',
    ],
  },
  {
    id: 'simplify',
    icon: '✂️',
    name: 'Simplify',
    command: '/simplify',
    category: 'code',
    categoryLabel: 'Code Quality',
    tagline: 'Refactor changed code for reuse, clarity, and efficiency',
    description:
      'Reviews changed code for simplification opportunities — duplicated logic, unnecessary abstractions, verbose patterns — then applies the fixes directly. Quality-only; does not hunt for bugs.',
    usage: '/simplify',
    examples: ['/simplify'],
    steps: [
      'Reviews recent changes for reuse and simplification opportunities',
      'Identifies duplicated logic, verbose patterns, unnecessary complexity',
      'Applies cleanups directly to the working tree',
    ],
    notes: 'For bug hunting, use /code-review instead.',
  },

  // ── Frontend ─────────────────────────────────────────────────────────────────
  {
    id: 'frontend-design',
    icon: '🎨',
    name: 'Frontend Design',
    command: '/frontend-design',
    category: 'frontend',
    categoryLabel: 'Frontend',
    tagline: 'Generate bold, production-grade UI components with CEC Track aesthetics',
    description:
      'Generates distinctive, polished React + Tailwind components matching the CEC Track design system: IBM Plex Sans body, Fraunces/Cormorant headings, deep navy primary, teal accent. Avoids generic AI aesthetics. Best followed by /simplify and accessibility checks.',
    usage: '/frontend-design [component description]',
    examples: [
      '/frontend-design Build a CE credit progress card — profession-coded color bar, circular progress ring, deadline urgency indicator',
      '/frontend-design Redesign the dashboard with a compliance summary hero card',
      '/frontend-design Build a credits log table with sticky header, sortable columns, mobile card view',
    ],
    steps: [
      'Reads component description and existing design system',
      'Generates component with bold visual hierarchy',
      'Applies IBM Plex Sans + Fraunces/Cormorant typography',
      'Includes all interactive states (hover, focus, active, disabled)',
      'Returns code; follow up with /simplify then accessibility review',
    ],
    notes: 'Always follow up with /simplify (spacing/typography cleanup) and check keyboard nav.',
  },

  // ── DevOps ───────────────────────────────────────────────────────────────────
  {
    id: 'verify',
    icon: '✅',
    name: 'Verify',
    command: '/verify',
    category: 'devops',
    categoryLabel: 'DevOps',
    tagline: 'Run the app and observe that a change actually works end-to-end',
    description:
      'Starts the dev server, navigates to the affected page or feature, tests the golden path and edge cases, and reports what it observed. Used to confirm a fix or feature before declaring it done.',
    usage: '/verify [what to check]',
    examples: [
      '/verify the free account cycle limit blocks a second cycle',
      '/verify the CE audit page loads and shows all 47 records',
      '/verify the upgrade banner appears for free accounts',
    ],
    steps: [
      'Starts backend and/or frontend dev servers',
      'Navigates to the relevant page/feature',
      'Tests the golden path + key edge cases',
      'Reports observed behavior with pass/fail assessment',
    ],
  },
  {
    id: 'run',
    icon: '▶️',
    name: 'Run',
    command: '/run',
    category: 'devops',
    categoryLabel: 'DevOps',
    tagline: 'Launch the app and screenshot the current state',
    description:
      'Starts the project (backend + frontend) and launches a browser to show the current running state. Use before /verify or to confirm a deployment-ready build.',
    usage: '/run',
    examples: ['/run'],
    steps: [
      'Detects project type (Express + React)',
      'Starts backend dev server (port 5000)',
      'Starts frontend dev server (port 3000)',
      'Opens browser and screenshots current state',
    ],
  },
  {
    id: 'email-mode',
    icon: '📩',
    name: 'Email Mode Toggle',
    command: '/email-mode',
    category: 'devops',
    categoryLabel: 'DevOps',
    tagline: 'Toggle email test mode on/off — restrict delivery to test accounts or open to all users',
    description:
      'Controls the DB-backed email test mode flag. When ON, all product transactional emails ' +
      '(cycle-setup, deadline reminders, team invites) are restricted to the allowed test email list ' +
      'and silently blocked for real users. When OFF, emails go to everyone. ' +
      'Takes effect instantly — no Render redeploy needed. ' +
      'Auth emails (OTP, password reset) are always plain text and are unaffected by this toggle.',
    usage: '/email-mode [on|off|status] [email1 email2 ...]',
    examples: [
      '/email-mode status',
      '/email-mode on archteam1@cectrack.com',
      '/email-mode on archteam1@cectrack.com jwjang@jangarchi.com',
      '/email-mode off',
    ],
    steps: [
      'Parses first token: on, off, or status',
      'Connects to MongoDB and reads/writes the SystemConfig "global" document',
      'on: sets emailTestMode=true and saves the allowed email list',
      'off: sets emailTestMode=false — all users can receive emails',
      'status: prints current mode and allowed email list',
      'Reports current state. No commit or Render redeploy needed.',
    ],
    notes:
      'Scope: product emails only (cycle-setup onboarding, cycle deadline reminders, team invites, company member emails). ' +
      'Auth emails (registration OTP, forgot-password, change-password) are always plain text and never affected by this toggle.',
  },
  {
    id: 'update-config',
    icon: '⚙️',
    name: 'Update Config',
    command: '/update-config',
    category: 'devops',
    categoryLabel: 'DevOps',
    tagline: 'Configure Claude Code settings, permissions, hooks, and env vars',
    description:
      'Updates .claude/settings.json or settings.local.json — adds permissions, configures hooks (automated behaviors triggered by events), sets env vars, and manages allowlists. Use for "from now on, always do X" type behaviors.',
    usage: '/update-config [what to configure]',
    examples: [
      '/update-config allow npm run build commands globally',
      '/update-config add a hook: after every commit, show git log --oneline -5',
      '/update-config set DEBUG=true in the environment',
    ],
    steps: [
      'Parses the configuration request',
      'Edits the appropriate settings.json file',
      'Confirms the change and shows the resulting config',
    ],
    notes: 'Automated/recurring behaviors (hooks) must be in settings.json — they cannot be stored in Claude memory.',
  },
];

// ─── Content Rotation ─────────────────────────────────────────────────────────

const ROTATION = [
  { angle: 'HSW / category-specific credit tracking',   done: true,  slug: '2026-02-25_hsw-credits-multi-state-tracking',   date: '2026-02-25' },
  { angle: 'Multi-state licensing complexity',           done: true,  slug: '2026-02-28_multi-license-complexity',            date: '2026-02-28' },
  { angle: 'Multi-profession (ARCH + PE + CPA + LAW)',   done: true,  slug: '2026-02-28_multi-license-complexity',            date: '2026-02-28' },
  { angle: 'Compliance dashboard / real-time tracking',  done: true,  slug: '2026-05-21_compliance-tracking',                 date: '2026-05-21' },
  { angle: 'Deadline reminders / email automation',      done: true,  slug: '2026-05-23_deadline-reminders',                  date: '2026-05-23' },
  { angle: 'Audit protection / certificate storage',     done: true,  slug: '2026-05-24_audit-certificate-storage',           date: '2026-05-24' },
  { angle: 'Profession spotlight — CPA',                 done: true,  slug: '2026-06-03_cpa-cpe-requirements',                date: '2026-06-03' },
  { angle: 'Profession spotlight — PE',                  done: true,  slug: '2026-06-03_pe-pdh-requirements',                 date: '2026-06-03' },
  { angle: 'Profession spotlight — ARCH',                done: true,  slug: '2026-06-03_arch-lu-requirements',             date: '2026-06-03' },
  { angle: 'Profession spotlight — LAW',                 done: true,  slug: '2026-06-03_law-cle-requirements',              date: '2026-06-03' },
  { angle: 'Profession spotlight — Nurse / Healthcare',  done: true,  slug: '2026-06-03_nursing-healthcare-ce',             date: '2026-06-03' },
  { angle: 'Onboarding / getting started',               done: true,  slug: '2026-06-03_getting-started-onboarding',        date: '2026-06-03' },
  { angle: 'Year-end compliance rush',                   done: true,  slug: '2026-06-03_year-end-compliance-rush',          date: '2026-06-03' },
  { angle: 'Team / firm-level license management',       done: true,  slug: '2026-06-03_team-firm-compliance',              date: '2026-06-03' },
  { angle: 'CE Audit — keeping requirements current',    done: true,  slug: '2026-06-03_ce-audit-requirements-accuracy',   date: '2026-06-03' },
  { angle: 'Profession spotlight — Dentist / DDS',       done: true,  slug: '2026-06-08_ny-dentist-ce-cycle',              date: '2026-06-08' },
  // ── Upcoming ────────────────────────────────────────────────────────────────
  { angle: 'Profession spotlight — Physical Therapist',  done: false, slug: '', date: '' },
  { angle: 'Profession spotlight — LCSW / Social Worker',done: false, slug: '', date: '' },
  { angle: 'State spotlight — California',               done: false, slug: '', date: '' },
  { angle: 'State spotlight — Texas',                    done: false, slug: '', date: '' },
  { angle: 'Solo practitioner — vs. the spreadsheet',   done: false, slug: '', date: '' },
  { angle: 'License renewal — what happens if you miss', done: false, slug: '', date: '' },
  { angle: 'Profession spotlight — LMHC / Counselor',   done: false, slug: '', date: '' },
  { angle: 'DDS national spotlight — 50-state rules',   done: false, slug: '', date: '' },
];

// ─── Web UI AI Tools ──────────────────────────────────────────────────────────

interface WebTool {
  id: string;
  icon: string;
  name: string;
  access: string;        // navigation path description
  accessLink: string;    // react-router path
  tagline: string;
  description: string;
  cost?: string;
  steps: { label: string; detail: string }[];
  fields: { name: string; options: string; desc: string }[];
  output: string;
  notes?: string;
}

const WEB_TOOLS: WebTool[] = [
  {
    id: 'onboarding-email',
    icon: '📬',
    name: 'Send Onboarding Email — Cycle Setup',
    access: 'Admin → Users → [select user] → View → ✉️ Send Onboarding Email',
    accessLink: '/admin/users',
    tagline: 'Preview and send a branded cycle-setup email to users who have licenses but no renewal cycle yet',
    description:
      'Appears automatically on any user\'s admin view page when they have at least one license but zero cycles. ' +
      'The button opens a modal with a live iframe preview of the exact email that will be sent — including their ' +
      'real name, license count, profession, and state names resolved from the database. ' +
      'Admin must check a confirmation box before the Send button becomes active. ' +
      'Email test mode (see /email-mode skill) controls whether delivery is open to all users or restricted to test accounts.',
    steps: [
      { label: 'Find the user',   detail: 'Go to Admin → Users → search for the user → click View. The "✉️ Send Onboarding Email" button appears in the action bar only when the user has ≥1 license and 0 cycles.' },
      { label: 'Preview',         detail: 'Click the button. A modal opens with a live iframe rendering the exact branded email — header image, dark navy gradient, step-by-step instructions, CTA button.' },
      { label: 'Confirm & send',  detail: 'Check the confirmation box ("I confirm I want to send this email to [Name] at [email]"), then click Send Email. The backend sends the email and the modal shows a success message.' },
    ],
    fields: [
      { name: 'Trigger condition', options: 'licenses.length ≥ 1 AND cycles.length === 0', desc: 'Button only appears when these conditions are met — no manual filtering required.' },
      { name: 'Email content',     options: 'Auto-generated from user record',             desc: 'First name, license count, profession ID, and state abbreviations are resolved live from the DB.' },
      { name: 'Confirmation box',  options: 'Required checkbox',                           desc: 'Admin must explicitly confirm the recipient name and email before Send is enabled.' },
      { name: 'Test mode gate',    options: 'Controlled by /email-mode skill',             desc: 'If email test mode is ON, the send will be blocked for real users and return a 403 error.' },
    ],
    output:
      'A branded transactional email delivered to the user\'s inbox. ' +
      'Subject: "Next step: set up your first renewal cycle on CEC Track". ' +
      'Content: 3-step guide (Create Cycle → Add Classes → Track Compliance), CTA button → /cycles, pro tip about multi-state licensing.',
    notes:
      'State names in the email body are resolved from the LicState MongoDB collection — not a hardcoded map. ' +
      'Use /email-mode status to check whether test mode is currently active before sending to real customers.',
  },
  {
    id: 'ai-email-generator',
    icon: '✉️',
    name: 'AI Email Generator',
    access: 'Admin → Marketing Email → Templates tab → ✨ AI Generate',
    accessLink: '/admin/marketing-email',
    tagline: 'GPT-4 writes a full branded email; gpt-image-1 generates a hero image — in-browser, 3-step flow',
    cost: '~$0.04/image if image is included (gpt-image-1 medium quality)',
    description:
      'Browser-based AI email generation built into the Marketing Email Admin page. ' +
      'GPT-4 writes complete responsive HTML using the CEC Track brand (navy header, logo-512.png, teal CTA, {FIRST}/{UNSUBSCRIBE_LINK} placeholders). ' +
      'Optionally generates a hero image via gpt-image-1 matched to the target audience and image style, uploads it to S3, and embeds the URL directly in the email. ' +
      'A live iframe preview lets you review the exact rendered email before saving.',
    steps: [
      { label: '1 — Open the form',    detail: 'Go to Marketing Email Admin → Templates tab → click ✨ AI Generate. Fill in topic, purpose, audience, tone, and optional context.' },
      { label: '2 — Generate',         detail: 'Click "✨ Generate Email". GPT-4 writes the HTML (~5 s). If image is enabled, gpt-image-1 generates and uploads it to S3 (~10–15 s total).' },
      { label: '3 — Preview',          detail: 'A live iframe renders the exact email. Review it, edit the template name, then click Save as Template — or Open in Editor to tweak the HTML manually.' },
    ],
    fields: [
      { name: 'Topic / Subject',     options: 'Free text',                                                         desc: 'The subject or main message. Be specific — "Year-end CE reminder for Texas CPAs" beats "reminder email".' },
      { name: 'Email Purpose',       options: 'Newsletter, Welcome, Reminder, Promotional, Announcement, Re-engagement, Campaign', desc: 'Shapes the overall structure and call-to-action of the email.' },
      { name: 'Target Audience',     options: 'All, ARCH, PE, CPA, LAW, RN, LA, LMHC, PHARM, DDS, General',     desc: 'GPT-4 tailors vocabulary and examples; image generator picks a profession-specific scene.' },
      { name: 'Tone',                options: 'Professional, Friendly & Warm, Urgent',                             desc: 'Controls the writing style and urgency of the copy.' },
      { name: 'Include AI Image',    options: 'Toggle on/off',                                                     desc: 'Adds a hero banner image. Costs ~$0.04 (gpt-image-1 medium). Image is placed after the email header.' },
      { name: 'Image Style',         options: 'Auto (random), Photorealistic, Infographic, Simple graphic',        desc: 'Auto picks randomly. See the Image Style Guide tab for details on each style.' },
      { name: 'Additional Context',  options: 'Free text (optional)',                                              desc: 'Extra instructions: "mention the CE Audit feature", "link to pricing page", "keep under 200 words", etc.' },
    ],
    output:
      'A saved MktEmail template in the database, ready to use in any Campaign. ' +
      'The template HTML includes {FIRST}, {LAST}, and {UNSUBSCRIBE_LINK} placeholders that the campaign sender replaces at send time.',
    notes:
      'The "Open in Editor" button loads the generated HTML into the manual template editor — useful for fine-tuning specific sections before saving. ' +
      'Image URLs are permanent S3 links (marketing-images/ prefix in the cectrack-certificates bucket). ' +
      'If image generation fails (e.g., gpt-image-1 policy block), the email is still saved — just without the hero image.',
  },
  {
    id: 'ai-linkedin-generator',
    icon: '📢',
    name: 'LinkedIn Content Generator',
    access: 'Admin Dashboard → LinkedIn',
    accessLink: '/admin/linkedin',
    tagline: 'GPT-4 writes a LinkedIn post; gpt-image-1 generates a matching image — in-browser, logged to a history panel',
    cost: '~$0.04/image (gpt-image-1 medium quality)',
    description:
      'Browser-based version of the /linkedin-post CLI skill. GPT-4 writes a 150–250 word post targeting architects, engineers, CPAs, and attorneys, ' +
      'then gpt-image-1 generates a matching image (photorealistic, infographic, or bold typographic) and uploads it to S3 under a permanent URL. ' +
      'Every generation is logged to a Post History panel — the web tool never auto-publishes to LinkedIn; publishing stays manual or via /linkedin-post.',
    steps: [
      { label: '1 — Fill in the form', detail: 'Enter a topic (required), optional additional context, and pick an image style.' },
      { label: '2 — Generate',         detail: 'Click "Generate Post + Image". GPT-4 writes the post (~5 s), then gpt-image-1 generates and uploads the image to S3 (~10–15 s total).' },
      { label: '3 — Copy & post',      detail: 'Copy the text and download the image, post to LinkedIn manually, then click "Mark as Posted" on the history entry to close the loop.' },
    ],
    fields: [
      { name: 'Topic',              options: 'Free text (required)',                              desc: 'What the post should be about.' },
      { name: 'Additional Context', options: 'Free text (optional)',                               desc: 'Specific points to highlight, tone preferences, or stats to include.' },
      { name: 'Image Style',        options: 'Photorealistic, Infographic, Bold Typographic',      desc: 'Same 3 styles as the /linkedin-post CLI skill.' },
    ],
    output:
      'A LinkedInPost record in the database (topic, post text, image URL, status) visible in the Post History panel, ' +
      'plus the post text and a permanent S3-hosted image ready to copy/download for manual posting.',
    notes:
      'Image URLs are permanent proxy links (linkedin-images/ prefix), not the old 7-day presigned S3 URLs. ' +
      'This tool only generates and logs content — it does not call the LinkedIn publish API. Use /linkedin-post --post for automated publishing.',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CAT_COLOR: Record<SkillCategory, string> = {
  content:  '#14b8a6',
  data:     '#3b82f6',
  code:     '#8b5cf6',
  frontend: '#f59e0b',
  devops:   '#6b7280',
};

const CAT_BG: Record<SkillCategory, string> = {
  content:  '#f0fdf4',
  data:     '#eff6ff',
  code:     '#faf5ff',
  frontend: '#fffbeb',
  devops:   '#f9fafb',
};

const CATEGORIES: { id: SkillCategory; label: string }[] = [
  { id: 'content',  label: 'Content' },
  { id: 'data',     label: 'Data' },
  { id: 'code',     label: 'Code Quality' },
  { id: 'frontend', label: 'Frontend' },
  { id: 'devops',   label: 'DevOps' },
];

const IMAGE_STYLES = [
  {
    key: 'photorealistic',
    icon: '📷',
    label: 'Photorealistic',
    desc: 'High-end corporate lifestyle photo. Profession-specific scenes — PE gets a bridge site, CPA gets a financial office, ARCH gets a construction interior.',
    prompt: 'gpt-image-1 scene derived from post topic + --accentLabel',
    example: '2026-06-03_pe-pdh-requirements.png',
  },
  {
    key: 'infographic',
    icon: '📊',
    label: 'Infographic',
    desc: 'Data visualization. Extracts numbers, states, and credit amounts from post text. White/gray background, navy + teal colors, bar charts, stat cards.',
    prompt: 'Numbers and states parsed from post text → chart layout prompt',
    example: null,
  },
  {
    key: 'simple',
    icon: '✏️',
    label: 'Simple Graphic',
    desc: 'Bold minimal editorial. First sentence of post becomes the hero text on a navy gradient. Profession-specific background motif at low opacity.',
    prompt: 'First sentence as hero → navy gradient + teal profession motif',
    example: null,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const AdminAITools: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [activeSkill, setActiveSkill] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'skills' | 'content' | 'images' | 'webtools'>('skills');
  const [activeWebTool, setActiveWebTool] = useState<string | null>(null);

  const copy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const postsPublished = ROTATION.filter(r => r.done).length;
  const anglesRemaining = ROTATION.filter(r => !r.done).length;

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: 1100 }}>

      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{ fontWeight: 800 }}>AI Agent Tools</h2>
          <p className="text-muted mb-0">
            Claude Code skills that research CE requirements, generate content, review code, and seed the database.
          </p>
        </div>
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">← Admin</Link>
      </div>

      {/* ── Stat chips ── */}
      <div className="d-flex flex-wrap gap-3 mb-4">
        {[
          { label: 'Skills available',   value: SKILLS.length,       color: '#0F1F3D' },
          { label: 'Posts published',    value: postsPublished,       color: '#14b8a6' },
          { label: 'Angles remaining',   value: anglesRemaining,      color: '#f59e0b' },
          { label: 'Image styles',       value: 3,                    color: '#8b5cf6' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', border: `1px solid #e5e7eb`, borderRadius: 10,
            padding: '10px 20px', display: 'flex', gap: 12, alignItems: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>{s.label}</span>
          </div>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to="/admin/ce-audit" className="btn btn-sm btn-outline-danger">🔍 CE Audit</Link>
          <Link to="/admin/ai-page-designs" className="btn btn-sm btn-outline-primary">🖌 AI Page Designs</Link>
          <a
            href="https://www.linkedin.com/company/111897979/admin/page-posts/published/"
            target="_blank" rel="noopener noreferrer"
            className="btn btn-sm btn-outline-secondary"
          >
            🔗 LinkedIn Page
          </a>
        </div>
      </div>

      {/* ── Explainer ── */}
      <div className="mb-4 p-4 rounded-3" style={{
        background: 'linear-gradient(135deg, #0f1f3d08, #3b82f610)',
        border: '1px solid #3b82f630',
      }}>
        <div className="d-flex gap-3 align-items-start">
          <span style={{ fontSize: '2rem', lineHeight: 1 }}>🤖</span>
          <div>
            <h6 className="mb-1 fw-bold">What are these?</h6>
            <p className="mb-2 text-muted small">
              <strong>Claude Code skills</strong> — specialized AI agents that live in
              <code className="mx-1">.claude/commands/</code>. Run them by typing
              <code className="mx-1">/skill-name</code> in the Claude Code sidebar or terminal.
              Each skill has a defined purpose, tool access, and rules it follows every time.
            </p>
            <p className="mb-0 text-muted small">
              They can search the web, read and write files, run DB scripts, call APIs, generate images, and commit — all with your confirmation at key steps.
            </p>
          </div>
        </div>
      </div>

      {/* ── How to run ── */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header" style={{ background: '#1e1e2e', color: '#cdd6f4' }}>
          <h6 className="mb-0">⚡ How to run a skill</h6>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            {[
              { n: '1', t: 'Open Claude Code', b: 'In VS Code, open the Claude sidebar. Or run claude in the terminal from the monorepo root.' },
              { n: '2', t: 'Type the command', b: 'Type /skill-name with any arguments. Hit Enter. Example: /research-ce LAW TX' },
              { n: '3', t: 'Review & confirm', b: 'The agent shows what it found or plans to do. Confirm before it touches the DB or APIs.' },
              { n: '4', t: 'Done', b: 'Agent commits, pushes, and reports back. Changes appear on cectrack.com after Render redeploys (~1–2 min).' },
            ].map(({ n, t, b }) => (
              <div key={n} className="col-12 col-md-6">
                <div className="d-flex gap-3">
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%', background: '#0F1F3D', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, flexShrink: 0, fontSize: '0.9rem',
                  }}>{n}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t}</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>{b}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded p-3" style={{ background: '#1e1e2e', fontFamily: 'monospace', fontSize: '0.85rem', color: '#cdd6f4' }}>
            <span style={{ color: '#89b4fa' }}>$</span> claude
            <span style={{ color: '#6c7086' }}> &nbsp;# open Claude Code</span><br />
            <span style={{ color: '#a6e3a1' }}>&gt; /prospect-list lawyer 10001 10017 10018</span>
            <span style={{ color: '#6c7086' }}> &nbsp;# build lawyer contact list across 3 ZIP codes → merged CSV</span><br />
            <span style={{ color: '#a6e3a1' }}>&gt; /research-ce PE TX,CA,FL</span>
            <span style={{ color: '#6c7086' }}> &nbsp;# research 3 states in parallel and seed DB</span><br />
            <span style={{ color: '#a6e3a1' }}>&gt; /research-ce DDS all</span>
            <span style={{ color: '#6c7086' }}> &nbsp;# all 50 states — 5 parallel agents</span><br />
            <span style={{ color: '#a6e3a1' }}>&gt; /linkedin-post [Architect]</span>
            <span style={{ color: '#6c7086' }}> &nbsp;# generate ARCH spotlight post + image</span><br />
            <span style={{ color: '#a6e3a1' }}>&gt; /code-review high</span>
            <span style={{ color: '#6c7086' }}> &nbsp;# review current branch diff for bugs</span>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <ul className="nav nav-tabs mb-4">
        {([
          { id: 'skills',   label: `Skills (${SKILLS.length})` },
          { id: 'webtools', label: `Web Tools (${WEB_TOOLS.length})` },
          { id: 'content',  label: `Content Rotation (${ROTATION.filter(r=>r.done).length}/${ROTATION.length})` },
          { id: 'images',   label: 'Image Style Guide' },
        ] as const).map(tab => (
          <li key={tab.id} className="nav-item">
            <button
              className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ═══════════════════════ TAB: SKILLS ═══════════════════════ */}
      {activeTab === 'skills' && (
        <>
          {CATEGORIES.map(cat => {
            const catSkills = SKILLS.filter(s => s.category === cat.id);
            if (!catSkills.length) return null;
            return (
              <div key={cat.id} className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span style={{
                    background: CAT_COLOR[cat.id], color: '#fff', padding: '2px 12px',
                    borderRadius: 100, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
                  }}>
                    {cat.label.toUpperCase()}
                  </span>
                  <hr style={{ flex: 1, margin: 0, borderColor: '#e5e7eb' }} />
                </div>

                <div className="d-flex flex-column gap-3">
                  {catSkills.map(skill => {
                    const isOpen = activeSkill === skill.id;
                    return (
                      <div key={skill.id} className="card shadow-sm" style={{
                        border: `1px solid ${CAT_COLOR[skill.category]}30`,
                        borderLeft: `4px solid ${CAT_COLOR[skill.category]}`,
                      }}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ fontSize: '1.3rem' }}>{skill.icon}</span>
                              <div>
                                <strong style={{ fontSize: '0.95rem' }}>{skill.name}</strong>
                                {skill.cost && (
                                  <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>{skill.cost}</span>
                                )}
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => setActiveSkill(isOpen ? null : skill.id)}
                            >
                              {isOpen ? 'Hide ▲' : 'Details ▼'}
                            </button>
                          </div>

                          <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{skill.tagline}</p>

                          {/* Usage */}
                          <div className="d-flex align-items-center gap-2 rounded p-2" style={{ background: '#1e1e2e' }}>
                            <code style={{ color: '#a6e3a1', fontFamily: 'monospace', fontSize: '0.9rem', flex: 1 }}>
                              {skill.usage}
                            </code>
                            <button
                              className="btn btn-sm btn-outline-light"
                              style={{ minWidth: 64, fontSize: '0.75rem' }}
                              onClick={() => copy(skill.usage, skill.id + '-u')}
                            >
                              {copied === skill.id + '-u' ? '✓ Copied' : 'Copy'}
                            </button>
                          </div>
                        </div>

                        {/* Expanded detail */}
                        {isOpen && (
                          <div className="card-body border-top" style={{ background: CAT_BG[skill.category] }}>
                            <div className="row g-4">
                              <div className="col-12 col-lg-6">
                                <div className="text-muted mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>What it does</div>
                                <p style={{ fontSize: '0.875rem' }}>{skill.description}</p>

                                <div className="text-muted mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Steps</div>
                                <ol className="ps-3 mb-0" style={{ fontSize: '0.82rem' }}>
                                  {skill.steps.map((s, i) => <li key={i} className="mb-1">{s}</li>)}
                                </ol>

                                {skill.flags && (
                                  <>
                                    <div className="text-muted mt-3 mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Flags</div>
                                    <table style={{ fontSize: '0.8rem', width: '100%' }}>
                                      <tbody>
                                        {skill.flags.map((f, i) => (
                                          <tr key={i}>
                                            <td style={{ paddingRight: 12, paddingBottom: 4 }}>
                                              <code style={{ color: '#8b5cf6' }}>{f.flag}</code>
                                            </td>
                                            <td style={{ color: '#6b7280', paddingBottom: 4 }}>{f.desc}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </>
                                )}

                                {skill.notes && (
                                  <div className="alert alert-warning mt-3 mb-0 py-2" style={{ fontSize: '0.8rem' }}>
                                    <strong>Note:</strong> {skill.notes}
                                  </div>
                                )}
                              </div>

                              <div className="col-12 col-lg-6">
                                <div className="text-muted mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Examples</div>
                                <div className="d-flex flex-column gap-2">
                                  {skill.examples.map((ex, i) => (
                                    <div key={i} className="d-flex align-items-center justify-content-between p-2 rounded" style={{ background: '#1e1e2e' }}>
                                      <code style={{ color: '#cba6f7', fontFamily: 'monospace', fontSize: '0.82rem', flex: 1, wordBreak: 'break-all' }}>
                                        {ex}
                                      </code>
                                      <button
                                        className="btn btn-sm btn-outline-light ms-2"
                                        style={{ minWidth: 52, fontSize: '0.72rem', flexShrink: 0 }}
                                        onClick={() => copy(ex, skill.id + '-ex-' + i)}
                                      >
                                        {copied === skill.id + '-ex-' + i ? '✓' : 'Copy'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Quick reference */}
          <div className="card shadow-sm mt-4">
            <div className="card-header"><h6 className="mb-0">Quick Reference</h6></div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Command</th>
                      <th>Arguments</th>
                      <th>Purpose</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody className="align-middle">
                    {SKILLS.map(skill => (
                      <tr key={skill.id}>
                        <td>
                          <code
                            style={{ color: CAT_COLOR[skill.category], cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => copy(skill.command, skill.id + '-qr')}
                            title="Click to copy"
                          >
                            {skill.command}
                          </code>
                          {copied === skill.id + '-qr' && <span className="text-muted small ms-1">✓</span>}
                        </td>
                        <td>
                          <code className="text-muted small">
                            {skill.usage.replace(skill.command, '').trim() || '—'}
                          </code>
                        </td>
                        <td className="small text-muted">{skill.tagline}</td>
                        <td>
                          <span style={{
                            background: CAT_COLOR[skill.category] + '18', color: CAT_COLOR[skill.category],
                            padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
                          }}>
                            {skill.categoryLabel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════ TAB: CONTENT ROTATION ═══════════════════════ */}
      {activeTab === 'content' && (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h6 className="mb-0 fw-bold">LinkedIn Content Rotation</h6>
              <p className="text-muted small mb-0">
                {postsPublished} of {ROTATION.length} angles published · {anglesRemaining} remaining
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                height: 10, width: 180, background: '#e5e7eb', borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', width: `${Math.round(postsPublished / ROTATION.length * 100)}%`,
                  background: '#14b8a6', borderRadius: 10,
                }} />
              </div>
              <span className="text-muted small">{Math.round(postsPublished / ROTATION.length * 100)}%</span>
            </div>
          </div>

          <div className="d-flex flex-column gap-2 mb-4">
            {ROTATION.map((r, i) => (
              <div key={i} className="d-flex align-items-center gap-3 p-3 rounded" style={{
                background: r.done ? '#f0fdf4' : '#f9fafb',
                border: `1px solid ${r.done ? '#bbf7d0' : '#e5e7eb'}`,
              }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{r.done ? '✅' : '⬜'}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: r.done ? 500 : 400, color: r.done ? '#166534' : '#374151', fontSize: '0.9rem' }}>
                    {r.angle}
                  </span>
                </div>
                {r.done && r.date && (
                  <span className="text-muted" style={{ fontSize: '0.78rem', flexShrink: 0 }}>{r.date}</span>
                )}
                {!r.done && (
                  <button
                    className="btn btn-sm"
                    style={{ background: '#0F1F3D', color: '#fff', fontSize: '0.75rem', flexShrink: 0 }}
                    onClick={() => copy(`/linkedin-post ${r.angle}`, 'rot-' + i)}
                  >
                    {copied === 'rot-' + i ? '✓ Copied' : 'Copy command →'}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Next angle suggestion */}
          {(() => {
            const next = ROTATION.find(r => !r.done);
            return next ? (
              <div className="rounded-3 p-4" style={{ background: 'linear-gradient(135deg, #0F1F3D, #1a3560)', color: '#fff' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', color: '#2dd4bf', marginBottom: 6, textTransform: 'uppercase' }}>
                  Next Recommended Post
                </div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>
                  {next.angle}
                </div>
                <div style={{ background: '#ffffff15', borderRadius: 8, padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.9rem', marginBottom: 12 }}>
                  /linkedin-post [{next.angle.split('—')[1]?.trim() ?? next.angle}]
                </div>
                <button
                  className="btn btn-sm"
                  style={{ background: '#14b8a6', color: '#fff', fontWeight: 600 }}
                  onClick={() => copy(`/linkedin-post [${next.angle.split('—')[1]?.trim() ?? next.angle}]`, 'next-angle')}
                >
                  {copied === 'next-angle' ? '✓ Copied!' : 'Copy command'}
                </button>
              </div>
            ) : (
              <div className="alert alert-success">
                🎉 All rotation angles have been covered! Consider adding new angles above.
              </div>
            );
          })()}
        </div>
      )}

      {/* ═══════════════════════ TAB: IMAGE STYLE GUIDE ═══════════════════════ */}
      {activeTab === 'images' && (
        <div>
          <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
            The <code>/linkedin-post</code> skill picks an image style <strong>at random after generating the post text</strong>, so the image generator can reference actual data from the post (state names, credit amounts, profession). Use <code>--imageStyle</code> to force a specific style.
          </p>

          <div className="row g-4 mb-4">
            {IMAGE_STYLES.map(s => (
              <div key={s.key} className="col-12 col-md-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-header d-flex align-items-center gap-2">
                    <span style={{ fontSize: '1.3rem' }}>{s.icon}</span>
                    <strong>{s.label}</strong>
                    <span className="ms-auto">
                      <code style={{ color: '#8b5cf6', fontSize: '0.8rem' }}>--imageStyle {s.key}</code>
                    </span>
                  </div>
                  <div className="card-body">
                    <p style={{ fontSize: '0.85rem' }}>{s.desc}</p>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      <strong>Prompt strategy:</strong> {s.prompt}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-header"><h6 className="mb-0">Profession → Scene Mapping (Photorealistic)</h6></div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Profession keyword</th>
                      <th>Scene generated</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: '0.82rem' }}>
                    {[
                      ['Engineer / PDH',          'Bridge or infrastructure site, hard hat, tablet showing compliance dashboard'],
                      ['Architect / LU',           'Construction site interior, reviewing drawings, hard hat in hand'],
                      ['Attorney / CLE',           'Modern law library or glass conference room, law books, laptop'],
                      ['CPA / CPE',                'Standing desk in financial office, laptop, city view through windows'],
                      ['Nurse / Healthcare',       'Hospital corridor or clinical workspace, tablet, scrubs or clinical attire'],
                      ['Dentist / DDS',            'Modern dental operatory or consultation room, reviewing CE certificates on a tablet'],
                      ['Audit / certificate',      'Organized desk with CE certificate folders, compliance documents'],
                      ['Deadline / renewal',       'Calendar and compliance documents on desk, relaxed and in control'],
                      ['Multi-state',              'Dual monitors showing multiple state license requirements'],
                      ['Category / credit',        'Compliance dashboard credit breakdown by category, taking notes'],
                      ['Everything else',          'Dual monitors, compliance dashboard, organized desk'],
                    ].map(([kw, scene]) => (
                      <tr key={kw}>
                        <td><code style={{ color: '#14b8a6' }}>{kw}</code></td>
                        <td className="text-muted">{scene}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Cost info */}
          <div className="alert alert-info d-flex gap-3 align-items-start">
            <span style={{ fontSize: '1.2rem' }}>💡</span>
            <div style={{ fontSize: '0.85rem' }}>
              <strong>Cost:</strong> gpt-image-1 (medium quality, 1536×1024) costs <strong>~$0.04 per image</strong>.
              Use <code>--svg</code> for a free SVG-based branded image (navy background, gold accents, text panels — no AI generation).
              The <code>--imageStyle</code> flag works with gpt-image-1 only; SVG always produces the branded template.
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════ TAB: WEB TOOLS ═══════════════════════ */}
      {activeTab === 'webtools' && (
        <div>
          <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
            Browser-based AI tools built directly into the admin UI — no terminal required.
            These run in your browser and call the backend AI endpoints (GPT-4, gpt-image-1).
          </p>

          {WEB_TOOLS.map(tool => {
            const isOpen = activeWebTool === tool.id;
            return (
              <div key={tool.id} className="card shadow-sm mb-4" style={{ border: '1px solid #14b8a630', borderLeft: '4px solid #14b8a6' }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: '1.4rem' }}>{tool.icon}</span>
                      <div>
                        <strong style={{ fontSize: '1rem' }}>{tool.name}</strong>
                        {tool.cost && <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>{tool.cost}</span>}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Link to={tool.accessLink} className="btn btn-sm btn-outline-success" style={{ fontSize: '0.75rem' }}>
                        Open →
                      </Link>
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.75rem' }}
                        onClick={() => setActiveWebTool(isOpen ? null : tool.id)}>
                        {isOpen ? 'Hide ▲' : 'Guide ▼'}
                      </button>
                    </div>
                  </div>

                  <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{tool.tagline}</p>

                  {/* Access path */}
                  <div className="d-flex align-items-center gap-2 rounded p-2" style={{ background: '#1e1e2e' }}>
                    <code style={{ color: '#a6e3a1', fontFamily: 'monospace', fontSize: '0.85rem', flex: 1 }}>
                      {tool.access}
                    </code>
                    <button className="btn btn-sm btn-outline-light" style={{ minWidth: 64, fontSize: '0.75rem' }}
                      onClick={() => copy(tool.access, tool.id + '-access')}>
                      {copied === tool.id + '-access' ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Expanded guide */}
                {isOpen && (
                  <div className="card-body border-top" style={{ background: '#f0fdf4' }}>
                    <div className="row g-4">

                      {/* Left — description + steps */}
                      <div className="col-12 col-lg-5">
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>What it does</div>
                        <p style={{ fontSize: '0.875rem' }}>{tool.description}</p>

                        <div className="text-muted mb-2" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>How to use — 3 steps</div>
                        <div className="d-flex flex-column gap-3">
                          {tool.steps.map((s, i) => (
                            <div key={i} className="d-flex gap-3 align-items-start">
                              <div style={{
                                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                background: '#14b8a6', color: '#fff', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem',
                              }}>{i + 1}</div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.label.replace(/^\d+ — /, '')}</div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{s.detail}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="text-muted mt-3 mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Output</div>
                        <p style={{ fontSize: '0.82rem' }}>{tool.output}</p>

                        {tool.notes && (
                          <div className="alert alert-warning py-2 mb-0" style={{ fontSize: '0.8rem' }}>
                            <strong>Note:</strong> {tool.notes}
                          </div>
                        )}
                      </div>

                      {/* Right — fields table */}
                      <div className="col-12 col-lg-7">
                        <div className="text-muted mb-2" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Form Fields</div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: '#e6faf8', borderBottom: '2px solid #14b8a630' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#0F1F3D', whiteSpace: 'nowrap' }}>Field</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#0F1F3D' }}>Options</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#0F1F3D' }}>Purpose</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tool.fields.map((f, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                  <td style={{ padding: '7px 10px', fontWeight: 600, color: '#0F1F3D', whiteSpace: 'nowrap' }}>{f.name}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#14b8a6' }}>{f.options}</span>
                                  </td>
                                  <td style={{ padding: '7px 10px', color: '#6b7280' }}>{f.desc}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Cost callout */}
                        {tool.cost && (
                          <div className="d-flex align-items-start gap-2 mt-3 p-3 rounded" style={{ background: '#fffbeb', border: '1px solid #fcd34d', fontSize: '0.82rem' }}>
                            <span>💡</span>
                            <div>
                              <strong>Cost:</strong> {tool.cost}. Toggle the "Include AI image" switch off to generate the email text only at no cost.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Footer ── */}
      <p className="text-muted text-center mt-5" style={{ fontSize: '0.78rem' }}>
        Skills live in <code>.claude/commands/</code> — edit any file there to change an agent's behavior.
        Last updated 2026-06-14 · {SKILLS.length} skills · {WEB_TOOLS.length} web tools.
      </p>
    </div>
  );
};

export default AdminAITools;
